import {
  ResolvedMethodAssignment,
  resolveMethodAssignment,
} from './methodAssignments'
import { InspectionArea } from './planningTypes'
import { scopeBaseUnit, scopeInUnit } from './scopeMeasurement'
import { ScopeType, TechniqueCatalogue } from './techniqueTypes'

export type InspectionScopeTotal = {
  key: string
  degradationTypeId: string | undefined
  techniqueId: string
  method: string
  scopeType: ScopeType
  locations: number
  estimated: number | undefined
}

export function getInspectionScopeTotals(
  areas: readonly InspectionArea[],
  catalogue: TechniqueCatalogue
): InspectionScopeTotal[] {
  const groups = new Map<
    string,
    { area: InspectionArea; method: ResolvedMethodAssignment }[]
  >()
  for (const area of areas) {
    for (const assignment of area.methodAssignments) {
      const method = resolveMethodAssignment(assignment, catalogue)
      if (!method.scopeType) continue
      const key = JSON.stringify([
        area.sourceZoneId,
        area.degradationTypeId,
        method.techniqueId,
        method.scopeType,
      ])
      groups.set(key, [...(groups.get(key) ?? []), { area, method }])
    }
  }
  return [...groups.entries()].flatMap(([key, members]) => {
    const first = members[0]
    const scopeType = first.method.scopeType
    if (!scopeType) return []
    const values = members.map(({ method }) =>
      scopeInUnit(method.estimatedScope, scopeType, scopeBaseUnit(scopeType))
    )
    return [
      {
        key,
        degradationTypeId: first.area.degradationTypeId,
        techniqueId: first.method.techniqueId,
        method: first.method.method,
        scopeType,
        locations: new Set(members.map(({ area }) => area.id)).size,
        estimated: values.every((value) => value !== undefined)
          ? values.reduce((sum, value) => sum + value, 0)
          : undefined,
      },
    ]
  })
}

export function getTargetDeviation(
  total: InspectionScopeTotal,
  requiredArea: number | undefined
) {
  if (
    total.scopeType !== 'area' ||
    total.estimated === undefined ||
    requiredArea === undefined
  )
    return undefined
  const difference = total.estimated - requiredArea
  if (Math.abs(difference) < 0.000001) return undefined
  return difference > 0 ? 'above' : 'below'
}
